/**
 * Web hooks for Stripe Connect account management
 */

import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface StripeConnectAccount {
  stripe_connect_account_id: string | null;
  stripe_connect_account_status: 'pending' | 'restricted' | 'active' | 'rejected' | null;
  stripe_connect_onboarding_complete: boolean;
  stripe_connect_onboarded_at: string | null;
}

/**
 * Hook: Get Stripe Connect account status for a company
 */
export function useStripeConnectAccount(companyId: string | null | undefined) {
  const [data, setData] = useState<StripeConnectAccount | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAccountStatus = useCallback(async () => {
    if (!companyId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('stripe_connect_account_id, stripe_connect_account_status, stripe_connect_onboarding_complete, stripe_connect_onboarded_at')
        .eq('id', companyId)
        .maybeSingle();
      
      if (companyError) {
        setError(companyError);
      } else {
        setData({
          stripe_connect_account_id: companyData?.stripe_connect_account_id || null,
          stripe_connect_account_status: companyData?.stripe_connect_account_status || null,
          stripe_connect_onboarding_complete: companyData?.stripe_connect_onboarding_complete || false,
          stripe_connect_onboarded_at: companyData?.stripe_connect_onboarded_at || null,
        } as StripeConnectAccount);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchAccountStatus();
  }, [fetchAccountStatus]);

  // Subscribe to realtime updates for this company's Stripe Connect status
  // This allows the UI to automatically update when the webhook updates the database
  useEffect(() => {
    if (!companyId) return;

    const channel = supabase
      .channel(`stripe-connect-${companyId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'companies',
          filter: `id=eq.${companyId}`,
        },
        (payload) => {
          // Only refetch if Stripe Connect fields changed
          const newRecord = payload.new as {
            stripe_connect_account_status?: string | null;
            stripe_connect_onboarding_complete?: boolean | null;
            stripe_connect_account_id?: string | null;
            stripe_connect_onboarded_at?: string | null;
          };
          const oldRecord = payload.old as {
            stripe_connect_account_status?: string | null;
            stripe_connect_onboarding_complete?: boolean | null;
            stripe_connect_account_id?: string | null;
            stripe_connect_onboarded_at?: string | null;
          };
          
          const stripeFieldsChanged = 
            newRecord.stripe_connect_account_status !== oldRecord.stripe_connect_account_status ||
            newRecord.stripe_connect_onboarding_complete !== oldRecord.stripe_connect_onboarding_complete ||
            newRecord.stripe_connect_account_id !== oldRecord.stripe_connect_account_id ||
            newRecord.stripe_connect_onboarded_at !== oldRecord.stripe_connect_onboarded_at;

          if (stripeFieldsChanged) {
            fetchAccountStatus();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, fetchAccountStatus]);

  return { data, isLoading, error, refetch: fetchAccountStatus };
}

/**
 * Hook: Create Stripe Connect account
 */
export function useCreateConnectAccount() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(async (companyId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Use supabase.functions.invoke() which handles CORS automatically
      const { data, error: invokeError } = await supabase.functions.invoke(
        'create-connect-account',
        {
          body: { companyId },
        }
      );

      if (invokeError) {
        throw new Error(invokeError.message || 'Failed to create Connect account');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create Connect account');
      }

      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error };
}

/**
 * Hook: Get Stripe Connect onboarding link
 */
export function useGetOnboardingLink() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const getLink = useCallback(async (companyId: string, refresh: boolean = false) => {
    setIsLoading(true);
    setError(null);

    try {
      // Use supabase.functions.invoke() which handles CORS automatically
      const { data, error: invokeError } = await supabase.functions.invoke(
        'get-connect-onboarding-link',
        {
          body: { companyId, refresh, platform: 'web' },
        }
      );

      if (invokeError) {
        throw new Error(invokeError.message || 'Failed to get onboarding link');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to get onboarding link');
      }

      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { getLink, isLoading, error };
}

/**
 * Hook: Refresh Stripe Connect account status from Stripe
 */
export function useRefreshConnectAccountStatus() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async (companyId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Use supabase.functions.invoke() which handles CORS automatically
      const { data, error: invokeError } = await supabase.functions.invoke(
        'refresh-connect-account-status',
        {
          body: { companyId },
        }
      );

      if (invokeError) {
        throw new Error(invokeError.message || 'Failed to refresh account status');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to refresh account status');
      }

      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { refresh, isLoading, error };
}

